import { HttpLink, ApolloLink, Observable, InMemoryCache, concat, split } from "@apollo/client";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import fetch from "cross-fetch";
import { CloseCode, createClient } from "graphql-ws";
import hooks from "../node_modules/moment/dist/moment.js";
import { map } from "rxjs";
import { ApolloClientProxy } from "./ApolloClientProxy.js";
class ApolloClientGraphQlTransportWsBuilder {
  _httpLink;
  _wsLink;
  _authLink;
  _tracingService;
  _cache;
  _typeDefs;
  _userManager;
  constructor(tracingService, userManager) {
    this._tracingService = tracingService;
    this._userManager = userManager;
  }
  withHttp(url) {
    this._httpLink = new HttpLink({
      uri: url,
      fetch
    });
    return this;
  }
  withWebsocket(url) {
    let tokenExpiryTimeout = null;
    let pingPongTimeout = null;
    const pongWaitInterval = 5e3;
    let activeSocket = null;
    const clientOptions = {
      url,
      connectionParams: (async function() {
        const user = await this._userManager.getUser();
        const token = user?.access_token ?? null;
        return { token: token ? `${token}` : "" };
      }).bind(this),
      keepAlive: 6e4,
      // how often ping message will be sent
      lazyCloseTimeout: 3e3,
      retryAttempts: 10,
      shouldRetry: () => true,
      on: {
        connected: (function(socket) {
          activeSocket = socket;
          if (tokenExpiryTimeout) {
            clearTimeout(tokenExpiryTimeout);
          }
          this.getExpiredTimeout().subscribe({
            next: (expiryTimeout) => {
              if (expiryTimeout > 0) {
                tokenExpiryTimeout = setTimeout(() => {
                  closeSocket();
                }, expiryTimeout);
              } else {
                closeSocket();
              }
            },
            error: () => closeSocket()
          });
          function closeSocket() {
            if (socket.readyState === WebSocket.OPEN) {
              socket.close(CloseCode.Forbidden, "Forbidden");
            }
          }
        }).bind(this),
        ping: (function(received) {
          if (!received) {
            pingPongTimeout = setTimeout(() => {
              if (activeSocket.readyState === WebSocket.OPEN) {
                activeSocket.close(CloseCode.ConnectionInitialisationTimeout, "Request Timeout");
              }
            }, pongWaitInterval);
          }
        }).bind(this),
        pong: (function(received) {
          if (received) {
            clearTimeout(pingPongTimeout);
          }
        }).bind(this)
      }
    };
    const client = createClient(clientOptions);
    this._wsLink = new GraphQLWsLink(client);
    return this;
  }
  withOidcAuth() {
    this._authLink = new ApolloLink((operation, forward) => {
      const extendAuthHeader = new Observable((observer) => {
        this._userManager.getUserExtended().subscribe((u) => {
          operation.setContext({
            headers: u.authHeaders
          });
          observer.next(operation);
          observer.complete();
        });
        return () => {
        };
      });
      return extendAuthHeader.flatMap((op) => forward(op));
    });
    return this;
  }
  withApiKeyAuth(apiKey) {
    this._authLink = new ApolloLink((operation, forward) => {
      operation.setContext({
        headers: {
          "x-api-key": apiKey
        }
      });
      return forward(operation);
    });
    return this;
  }
  withAuth(authType, option) {
    switch (authType) {
      case "oidc":
        this.withOidcAuth();
        break;
      case "api-key":
        this.withApiKeyAuth(option?.apiKey);
        break;
    }
    return this;
  }
  withClientMocking(cache, typeDefs) {
    this._cache = cache;
    this._typeDefs = typeDefs;
    return this;
  }
  buildClient() {
    const defaultOptions = {
      query: {
        errorPolicy: "all",
        fetchPolicy: this._cache ? "network-only" : "no-cache"
      },
      mutate: {
        errorPolicy: "all"
      }
    };
    const client = new ApolloClientProxy(
      {
        link: this.getLink(),
        cache: this._cache ?? new InMemoryCache({
          resultCaching: false
        }),
        defaultOptions,
        typeDefs: this._typeDefs
      },
      this._tracingService,
      this._userManager
    );
    return client;
  }
  getLink() {
    const httpLink = this._authLink ? concat(this._authLink, this._httpLink) : this._httpLink;
    const finalLink = this._wsLink ? split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === "OperationDefinition" && definition.operation === "subscription";
      },
      this._wsLink,
      httpLink
    ) : httpLink;
    return finalLink;
  }
  getExpiredTimeout() {
    return this._userManager.getUserObservable().pipe(
      map((user) => {
        const expiryTimeout = hooks(new Date(user.expires_at * 1e3)).diff(hooks(), "milliseconds");
        return expiryTimeout;
      })
    );
  }
}
export {
  ApolloClientGraphQlTransportWsBuilder
};
//# sourceMappingURL=apolloClientBuilder.js.map
